import { FormInstance } from 'antd';
import { CONTENT_API_PATH } from 'constants/api';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from 'type/apiType';
import { ElevenDbContentInterface, MovieInterface, MovieListItem } from '../types/movieType';

class MovieService {
  constructor(private apiService: ApiService<MovieInterface>) {}

  async fetch(query: URLSearchParams): Promise<FetchResult<MovieInterface>> {
    query.set('content_type', 'movie');
    return await this.apiService.fetch(query);
  }

  /**
   * Fetch Movie list with minimal fields for list page
   */
  async fetchList(query: URLSearchParams): Promise<FetchResult<MovieListItem>> {
    query.set('content_type', 'movie');
    // Request only fields needed for list (thumbnails are automatically included by backend)
    const fields = ['id', 'slug', 'status', 'i18n_title', 'title', 'title_en', 'thumbnails'].join(
      ','
    );
    query.set('fields', fields);
    return await this.apiService.fetch(query);
  }

  async fetchById(id: number): Promise<MovieInterface> {
    return await this.apiService.fetchById(id);
  }

  async create(form: FormInstance, customData?: Partial<MovieInterface>): Promise<MovieInterface> {
    const formData = form.getFieldsValue();
    const mergedData = { ...formData, ...customData };

    // Ensure i18n fields are included
    if (!mergedData.i18n_title) mergedData.i18n_title = {};
    if (!mergedData.i18n_synopsis) mergedData.i18n_synopsis = {};
    if (!mergedData.i18n_seo_title) mergedData.i18n_seo_title = {};
    if (!mergedData.i18n_seo_keyword) mergedData.i18n_seo_keyword = {};
    if (!mergedData.i18n_seo_synopsis) mergedData.i18n_seo_synopsis = {};

    const response = await this.apiService.create(mergedData);
    return response;
  }

  async update(
    id: number,
    form: FormInstance,
    customData?: Partial<MovieInterface>
  ): Promise<MovieInterface> {
    const formData = form.getFieldsValue();
    const mergedData = { ...formData, ...customData };

    const response = await this.apiService.update(id, mergedData);
    return response;
  }

  async delete(id: number): Promise<void> {
    await this.apiService.delete(id);
  }

  async fetchElevenDbContent(contentId: number): Promise<ElevenDbContentInterface> {
    const response = await this.apiService.fetchChildItemsByParentId<ElevenDbContentInterface>(
      contentId,
      'eleven-db/'
    );
    return response as ElevenDbContentInterface;
  }

  async updateElevenDbContent(
    contentId: number,
    elevenDbContent: Partial<ElevenDbContentInterface>
  ): Promise<void> {
    await this.apiService.updateItemsForParent(contentId, 'eleven-db/', elevenDbContent);
  }

  async createElevenDbContent(
    contentId: number,
    elevenDbContent: ElevenDbContentInterface
  ): Promise<void> {
    await this.apiService.createItemsForParent(contentId, 'eleven-db/', elevenDbContent);
  }
}

export const movieService = new MovieService(new ApiService<MovieInterface>(CONTENT_API_PATH));
